// ============================================
// sendInitialGreeting — Realtime data-channel onopen handler
// ============================================
// Wave 5e3 — extracted from initializeWebRTC().dataChannel.onopen body.
//
// Sequence on data channel open:
//   1. Send `session.update` with system instructions, locked transcription
//      language, voice, and tool catalog.
//   2. Pre-mute the microphone so the bot can deliver the full opening
//      greeting without being interrupted by user audio.
//   3. After GREETING_TRIGGER_DELAY_MS:
//        a. Push a synthetic `conversation.item.create` "user" message
//           that explicitly asks the bot to speak its OPENING GREETING
//           (no function calls).
//        b. Send `response.create` to trigger the actual response.
//        c. Schedule a GREETING_RETRY_TIMEOUT_MS retry if no audio is
//           produced and no response is still active. Skip retry if a
//           response is in-flight (audio just hasn't started yet).
//
// Caller responsibilities (kept inline):
//   - Wiring `dataChannel.onmessage = handleDataChannelMessage`.
//   - SDP negotiation (Wave 5e1) and post-connect state writes.

import type { MutableRefObject } from 'react'
import { TIMING } from '../constants/voice-bot-timing'

// Only models deployed in this Azure OpenAI resource and verified to emit
// `conversation.item.input_audio_transcription.completed` with a plain
// `transcript` may be selected here. Catalog support alone is insufficient:
// an undeployed model silently disables captions while the voice session keeps
// running. Unknown/omitted values therefore fall back to the verified default.
const ALLOWED_TRANSCRIBE_MODELS: readonly string[] = [
  'gpt-4o-transcribe',
  'gpt-realtime-whisper',
]
const DEFAULT_TRANSCRIBE_MODEL = 'gpt-realtime-whisper'
function resolveTranscribeModel(model: unknown): string {
  return typeof model === 'string' && ALLOWED_TRANSCRIBE_MODELS.includes(model)
    ? model
    : DEFAULT_TRANSCRIBE_MODEL
}

// A/B override for the caller-transcript model, read at session start.
// Priority: URL `?transcribeModel=` > localStorage `ilandsTranscribeModel`.
// Set by the demo login picker; absent on real customer embeds. Validated by
// resolveTranscribeModel, so an unknown value harmlessly falls back to default.
function getTranscribeModelOverride(): string | null {
  try {
    if (typeof window === 'undefined') return null
    const fromUrl = new URLSearchParams(window.location.search).get('transcribeModel')
    if (fromUrl) return fromUrl
    return window.localStorage.getItem('ilandsTranscribeModel')
  } catch {
    return null
  }
}

interface SessionTools {
  tools: unknown
}

export interface SendInitialGreetingDeps {
  dataChannel: RTCDataChannel
  peerConnection: RTCPeerConnection
  contextPropertyName: string | null
  // `any` here lets the caller pass its full VoiceBotConfig without
  // duplicating the whole shape — only `voice` + `greeting` are read.
  activeConfig: any
  sessionData: SessionTools
  selectedLanguageRef: MutableRefObject<string>
  greetingRetryTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
  responseHadAudioRef: MutableRefObject<boolean>
  activeResponseRef: MutableRefObject<unknown>
  buildSystemInstructions: (
    contextPropertyName?: string | null,
    configOverride?: any,
  ) => string
  /**
   * Cross-channel handoff: prior conversation turns to inject into the
   * Realtime session as `conversation.item.create` events before any
   * model response. When present (and non-empty), the canonical opening
   * greeting is SKIPPED and a continuation prompt is sent instead so the
   * bot acknowledges the user briefly and waits for them to speak next.
   * Order matters: items are sent in array order to preserve conversation
   * flow.
   */
  priorMessages?: Array<{ role: 'user' | 'assistant'; content: string }>
}

export const sendInitialGreeting = (deps: SendInitialGreetingDeps): void => {
  const {
    dataChannel,
    peerConnection,
    contextPropertyName,
    activeConfig,
    sessionData,
    selectedLanguageRef,
    greetingRetryTimeoutRef,
    responseHadAudioRef,
    activeResponseRef,
    buildSystemInstructions,
    priorMessages,
  } = deps

  console.log('✅ Data channel open')

  // Configure session (using activeConfig passed to this function)
  // REGULATORY PHASE: Force high silence_duration to prevent interruption during disclosure
  // The bot must complete the regulatory statement without being interrupted
  // GA Protocol schema: voice under audio.output.voice, temperature not in session
  const sessionConfig = {
    type: 'session.update',
    session: {
      type: 'realtime', // Required by Azure OpenAI GA Protocol
      instructions: buildSystemInstructions(contextPropertyName, activeConfig),
      audio: {
        input: {
          // Phase B: when the customer uses the Azure Speech streaming
          // transcript, disable the Realtime built-in transcription entirely
          // (the widget drives the displayed user transcript from the Azure
          // Speech SDK instead — avoids paying for two ASRs). Otherwise use
          // gpt-realtime-whisper, validated in Hebrew on the preview deployment.
          ...(activeConfig.liveTranscriptEngine === 'azure_speech'
            ? {}
            : {
                transcription: {
                  // Per-session A/B override (demo picker) → per-customer
                  // config → default; always validated.
                  model: resolveTranscribeModel(
                    getTranscribeModelOverride() || activeConfig.realtimeTranscribeModel,
                  ),
                  // Lock to user's selected language from pre-call picker
                  language: selectedLanguageRef.current,
                },
              }),
        },
        output: {
          voice: activeConfig.voice,
        },
      },
      // Note: Azure GA Protocol WebRTC session.update only supports limited fields
      // modalities and turn_detection are configured at session creation (API side)
      tools: sessionData.tools,
    },
  }
  dataChannel.send(JSON.stringify(sessionConfig))
  console.log(
    '🎙️ Caller-transcript model:',
    activeConfig.liveTranscriptEngine === 'azure_speech'
      ? 'azure_speech (Azure Speech SDK)'
      : resolveTranscribeModel(getTranscribeModelOverride() || activeConfig.realtimeTranscribeModel),
  )
  console.log(
    '📤 System instructions sent:',
    `${buildSystemInstructions(contextPropertyName, activeConfig).substring(0, 200)}...`,
  )

  // Log the actual greeting that will be used (with property context if available)
  const actualGreeting = contextPropertyName
    ? activeConfig.greeting.replace(
        'אז עם מי יש לי את הכבוד?',
        `אני רואה שאתה מתעניין ב${contextPropertyName}! אז עם מי יש לי את הכבוד?`,
      )
    : activeConfig.greeting
  console.log('📤 Greeting configured:', actualGreeting)

  // Mute microphone BEFORE triggering greeting to prevent interruption
  // This ensures the bot can complete the full greeting without user audio interference
  peerConnection.getSenders().forEach((sender) => {
    if (sender.track?.kind === 'audio') {
      sender.track.enabled = false
      console.log('🎤 Mic pre-muted for initial greeting')
    }
  })

  const hasPriorContext = Array.isArray(priorMessages) && priorMessages.length > 0

  // Send explicit greeting instruction after delay
  // We MUST send a message to trigger the greeting, otherwise the model might call a function instead
  setTimeout(() => {
    // ----- Cross-channel handoff path -----
    // When the user switched into voice mid-conversation (from chat or
    // a prior voice session within the same widget shell), seed the
    // Realtime conversation with the prior turns and SKIP the canonical
    // opening greeting. Instead, prompt the model to briefly acknowledge
    // the user and wait for them to speak. This keeps the handoff
    // transparent — no repeated greeting, no lost context.
    if (hasPriorContext) {
      if (dataChannel.readyState !== 'open') return
      // Inject prior turns in order. Realtime accepts both user and
      // assistant role messages via conversation.item.create, which
      // populates the model's context window for the next response.
      for (const msg of priorMessages!) {
        const content = (msg.content || '').trim()
        if (!content) continue
        dataChannel.send(
          JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: msg.role,
              content: [
                {
                  // Realtime API content-type rules:
                  //   role=user     → 'input_text'
                  //   role=assistant→ 'output_text'
                  // Sending 'text' on an assistant item triggers
                  // invalid_request_error ("Value must be 'output_text'").
                  type: msg.role === 'user' ? 'input_text' : 'output_text',
                  text: content,
                },
              ],
            },
          }),
        )
      }
      // Continuation cue: do NOT replay the greeting; briefly acknowledge
      // the channel switch and wait for the user to speak. The bot should
      // pick up exactly where the prior session left off.
      dataChannel.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text:
                  '[SYSTEM: The user just switched to voice mid-conversation. ' +
                  'The prior turns above are the full conversation so far. ' +
                  'Acknowledge the switch in ONE short sentence (e.g. "I\'m here, please continue") ' +
                  'and wait for the user to speak. Do NOT repeat the opening greeting. ' +
                  'Do NOT call any functions yet. Continue exactly where the prior session left off.]',
              },
            ],
          },
        }),
      )
      dataChannel.send(JSON.stringify({ type: 'response.create' }))
      console.log(
        `📤 Cross-channel handoff: injected ${priorMessages!.length} prior turn(s), skipped greeting`,
      )
      // No retry watchdog here — the bot's first response is a short
      // acknowledgement, not the multi-sentence regulatory greeting.
      return
    }

    // ----- Standard greeting path (fresh session, no prior context) -----
    // Helper to send greeting request
    const sendGreetingRequest = (isRetry = false) => {
      if (dataChannel.readyState !== 'open') return

      // First, send a system message explicitly requesting the greeting
      dataChannel.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              {
                type: 'input_text',
                text: isRetry
                  ? '[SYSTEM: You went silent. Please say your OPENING GREETING now to welcome the customer.]'
                  : '[SYSTEM: A new customer just connected. Start the conversation NOW by saying your OPENING GREETING exactly as written in your instructions. Do NOT call any functions - just greet the customer.]',
              },
            ],
          },
        }),
      )
      // Then trigger the response
      dataChannel.send(JSON.stringify({ type: 'response.create' }))
      console.log(
        `📤 ${isRetry ? 'RETRY: ' : ''}Triggered greeting response with explicit instruction`,
      )
    }

    // Send initial greeting
    sendGreetingRequest(false)

    // Set up retry timeout in case greeting produces no audio
    greetingRetryTimeoutRef.current = setTimeout(() => {
      if (
        !responseHadAudioRef.current &&
        !activeResponseRef.current &&
        dataChannel.readyState === 'open'
      ) {
        console.log(
          '⚠️ Initial greeting had no audio and no active response - retrying...',
        )
        sendGreetingRequest(true)
      } else if (!responseHadAudioRef.current && activeResponseRef.current) {
        console.log(
          '⏳ Initial greeting has no audio yet but response still active - skipping retry',
        )
      }
      greetingRetryTimeoutRef.current = null
    }, TIMING.GREETING_RETRY_TIMEOUT_MS)
  }, TIMING.GREETING_TRIGGER_DELAY_MS)
}
